import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CircularProgress, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent, DialogTitle, IconButton, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Grid } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { apiCall } from '../main/ApiCall';
import '../css/main/Home.css';
import "../css/others/Table.css";
import { buildUserPayload } from '../main/buildUserPayload';
import CloseIcon from '@mui/icons-material/Close';
import { AuditScore, ProjectRadarData, AuditScoresResponse, ControlScore } from "../interFaces/interfaces";
const QCDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [additionalData, setAdditionalData] = useState<any>(null);
  const [auditScoresData] = useState<AuditScoresResponse | null>(null);
  const [loading, setLoading] = useState({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, additionalSummary: true, additionalFindings: true, auditScores: true, normalAuditScores: false, integratedAuditScores: false });
  const [errors, setErrors] = useState({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, additionalSummary: false, additionalFindings: false, auditScores: false, normalAuditScores: false, integratedAuditScores: false });
  const [selectedProject, setSelectedProject] = useState<{ projectId: string, auditId: string, projectName: string, campaignId: string, campaignName: string, checklistType: string } | null>(null);
  const [radarDialogOpen, setRadarDialogOpen] = useState(false);
  const [findingsDialogOpen, setFindingsDialogOpen] = useState(false);
  const [selectedProjectRadarData, setSelectedProjectRadarData] = useState<ProjectRadarData[]>([]);
  const [projectFindingsData, setProjectFindingsData] = useState<any>(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [integratedFindingsData, setIntegratedFindingsData] = useState<any>(null);
  const [integratedFindingsLoading, setIntegratedFindingsLoading] = useState(false);
  const [normalCurrentPage, setNormalCurrentPage] = useState<number>(1);
  const [normalItemsPerPage, setNormalItemsPerPage] = useState<number>(5);
  const [normalAuditScores, setNormalAuditScores] = useState<AuditScore[]>([]);
  const [normalTotalElements, setNormalTotalElements] = useState<number>(0);
  const [normalTotalPages, setNormalTotalPages] = useState<number>(0);
  const [normalLoading, setNormalLoading] = useState<boolean>(false);
  const [integratedCurrentPage, setIntegratedCurrentPage] = useState<number>(1);
  const [integratedItemsPerPage, setIntegratedItemsPerPage] = useState<number>(5);
  const [integratedAuditScores, setIntegratedAuditScores] = useState<AuditScore[]>([]);
  const [integratedTotalElements, setIntegratedTotalElements] = useState<number>(0);
  const [integratedTotalPages, setIntegratedTotalPages] = useState<number>(0);
  const [integratedLoading, setIntegratedLoading] = useState<boolean>(false);
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState<string>("all");
  const [allCampaignNames, setAllCampaignNames] = useState<string[]>([]);
  const clientId = localStorage.getItem("clientId");
  const isMounted = useRef(true);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const COLORS = ['#FF6384', '#FF9F40', '#FFCD56', '#4BC0C0', '#36A2EB', '#9966FF', '#6d1629', '#C9CBCF'];
  const LoadingSpinner = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <CircularProgress />
    </Box>
  );
  const getUniqueCampaignNames = () => {
    if (!auditScoresData?.content && allCampaignNames.length === 0) return [];
    if (allCampaignNames.length > 0) return allCampaignNames;
    if (auditScoresData?.content) {
      const campaignNames = new Set<string>();
      auditScoresData.content.forEach((auditScore: AuditScore) => {
        if (auditScore.campaignName) {
          campaignNames.add(auditScore.campaignName);
        }
      });
      return Array.from(campaignNames).sort();
    }
    return [];
  };
  const fetchNormalAuditScores = useCallback(async (campaignFilter?: string, page?: number, size?: number) => {
    const campaign = campaignFilter !== undefined ? campaignFilter : selectedCampaignFilter;
    const currentPage = page !== undefined ? page : normalCurrentPage;
    const currentSize = size !== undefined ? size : normalItemsPerPage;
    try {
      setNormalLoading(true);
      setErrors(prev => ({ ...prev, normalAuditScores: false }));
      const campaignParam = campaign !== "all" ? `&campaignName=${encodeURIComponent(campaign)}` : "";
      const normalData = await apiCall("get", `/api/dashboard/audit-scores?clientId=${clientId}&checklistType=NORMAL&page=${currentPage - 1}&size=${currentSize}${campaignParam}`);
      if (isMounted.current && normalData) {
        setNormalTotalPages(normalData.totalPages);
        setNormalTotalElements(normalData.totalElements);
        setNormalAuditScores(normalData.content || []);
        if (normalData.content?.length) {
          setAllCampaignNames(prev => {
            const combined = new Set([...prev, ...normalData.content.map((a: AuditScore) => a.campaignName).filter(Boolean)]);
            return Array.from(combined).sort();
          });
        }
        const timeoutId = setTimeout(() => {
          if (isMounted.current) setNormalLoading(false);
        }, 300);
        timeoutRefs.current.push(timeoutId);
      }
    } catch (err) {
      if (isMounted.current) {
        setErrors(prev => ({ ...prev, normalAuditScores: true }));
        setNormalLoading(false);
      }
    }
  }, [clientId, selectedCampaignFilter, normalCurrentPage, normalItemsPerPage]);
  const fetchIntegratedAuditScores = useCallback(async (campaignFilter?: string, page?: number, size?: number) => {
    const campaign = campaignFilter !== undefined ? campaignFilter : selectedCampaignFilter;
    const currentPage = page !== undefined ? page : integratedCurrentPage;
    const currentSize = size !== undefined ? size : integratedItemsPerPage;
    try {
      setIntegratedLoading(true);
      setErrors(prev => ({ ...prev, integratedAuditScores: false }));
      const campaignParam = campaign !== "all" ? `&campaignName=${encodeURIComponent(campaign)}` : "";
      const integratedData = await apiCall("get", `/api/dashboard/audit-scores?clientId=${clientId}&checklistType=INTEGRATED&page=${currentPage - 1}&size=${currentSize}${campaignParam}`);
      if (isMounted.current && integratedData) {
        setIntegratedTotalPages(integratedData.totalPages);
        setIntegratedTotalElements(integratedData.totalElements);
        setIntegratedAuditScores(integratedData.content || []);
        if (integratedData.content?.length) {
          setAllCampaignNames(prev => {
            const combined = new Set([...prev, ...integratedData.content.map((a: AuditScore) => a.campaignName).filter(Boolean)]);
            return Array.from(combined).sort();
          });
        }
        const timeoutId = setTimeout(() => {
          if (isMounted.current) setIntegratedLoading(false);
        }, 300);
        timeoutRefs.current.push(timeoutId);
      }
    } catch (err) {
      if (isMounted.current) {
        setErrors(prev => ({ ...prev, integratedAuditScores: true }));
        setIntegratedLoading(false);
      }
    }
  }, [clientId, selectedCampaignFilter, integratedCurrentPage, integratedItemsPerPage]);
  useEffect(() => {
    fetchNormalAuditScores();
  }, [fetchNormalAuditScores]);
  useEffect(() => {
    fetchIntegratedAuditScores();
  }, [fetchIntegratedAuditScores]);
  const handleCampaignFilterChange = (event: SelectChangeEvent) => {
    const newFilter = event.target.value;
    setSelectedCampaignFilter(newFilter);
    setNormalCurrentPage(1);
    setIntegratedCurrentPage(1);
    fetchNormalAuditScores(newFilter, 1, normalItemsPerPage);
    fetchIntegratedAuditScores(newFilter, 1, integratedItemsPerPage);
  };
  const getRatingClassName = (rating: string) => {
    if (!rating) return "rating-default";
    switch (rating.toLowerCase()) {
      case "high risk":
        return "rating-high-risk";
      case "needs improvement":
        return "rating-needs-improvement";
      case "satisfactory":
        return "rating-satisfactory";
      case "effective":
        return "rating-effective";
      case "optimized":
        return "rating-optimized";
      default:
        return "rating-default";
    }
  };
  const getControlScoreByType = (auditScore: AuditScore, controlType: string) => {
    if (!auditScore?.auditScoreData?.controlScores || !Array.isArray(auditScore.auditScoreData.controlScores)) {
      return undefined;
    }
    return auditScore.auditScoreData.controlScores.find(
      (control: ControlScore) => control?.controlType === controlType
    );
  };
  const getAllControlTypes = (auditScores: AuditScore[]) => {
    if (!auditScores) return [];
    const allControlTypes = new Set<string>();
    auditScores.forEach((auditScore: AuditScore) => {
      if (auditScore?.auditScoreData?.controlScores && Array.isArray(auditScore.auditScoreData.controlScores)) {
        auditScore.auditScoreData.controlScores.forEach((control: ControlScore) => {
          if (control?.controlType) {
            allControlTypes.add(control.controlType);
          }
        });
      }
    });
    return Array.from(allControlTypes);
  };
  const formatWeightedScore = (weightedScore: number): string => {
    return `${weightedScore}%`;
  };
  const fetchFindingsData = async (campaignId: string, projectId: string) => {
    try {
      setFindingsLoading(true);
      const response = await apiCall("get", `/api/dashboard/audit-scores/risk?campaignId=${campaignId}&projectId=${projectId}`);
      if (isMounted.current) {
        setProjectFindingsData(response);
        setFindingsDialogOpen(true);
      }
    } catch (error) {
      console.error("Error fetching findings data:", error);
    } finally {
      setFindingsLoading(false);
    }
  };
  const fetchIntegratedFindingsData = async (campaignId: string, projectId: string) => {
    try {
      setIntegratedFindingsLoading(true);
      const response = await apiCall("get", `/api/dashboard/audit-scores/risk?campaignId=${campaignId}&projectId=${projectId}`);
      if (isMounted.current) {
        setIntegratedFindingsData(response);
      }
    } catch (error) {
      console.error("Error fetching integrated findings data:", error);
    } finally {
      setIntegratedFindingsLoading(false);
    }
  };
  const handleProjectClick = (projectId: string, auditId: string, projectName: string, checklistType: string, campaignId: string, campaignName: string) => {
    setSelectedProject({ projectId, auditId, projectName, campaignId, campaignName, checklistType });
    if (checklistType === "NORMAL") {
      fetchFindingsData(campaignId, projectId);
    } else {
      const projectAudit = integratedAuditScores.find((audit: AuditScore) => audit.auditId === auditId && audit.projectId === projectId);
      if (projectAudit) {
        const auditScoreData = projectAudit.auditScoreData as any;
        const controlScores: ControlScore[] = auditScoreData?.controlScores || [];
        const radarData = controlScores.map((control: ControlScore) => ({ subject: control.controlType, score: control.score, weightedScore: control.weightedScore, fullMark: 5 }));
        setSelectedProjectRadarData(radarData);
        fetchIntegratedFindingsData(campaignId, projectId);
        setRadarDialogOpen(true);
      }
    }
  };
  const handleCloseRadarDialog = () => {
    setRadarDialogOpen(false);
    setSelectedProject(null);
    setSelectedProjectRadarData([]);
    setIntegratedFindingsData(null);
  };
  const handleCloseFindingsDialog = () => {
    setFindingsDialogOpen(false);
    setSelectedProject(null);
    setProjectFindingsData(null);
  };
  const handleFirstPageNormal = () => {
    if (normalCurrentPage !== 1) {
      setNormalCurrentPage(1);
      fetchNormalAuditScores(selectedCampaignFilter, 1, normalItemsPerPage);
    }
  };
  const handleLastPageNormal = () => {
    const lastPage = normalTotalPages;
    if (normalCurrentPage !== lastPage && lastPage > 0) {
      setNormalCurrentPage(lastPage);
      fetchNormalAuditScores(selectedCampaignFilter, lastPage, normalItemsPerPage);
    }
  };
  const handleNextPageNormal = () => {
    if (normalCurrentPage < normalTotalPages) {
      const nextPage = normalCurrentPage + 1;
      setNormalCurrentPage(nextPage);
      fetchNormalAuditScores(selectedCampaignFilter, nextPage, normalItemsPerPage);
    }
  };
  const handlePreviousPageNormal = () => {
    if (normalCurrentPage > 1) {
      const prevPage = normalCurrentPage - 1;
      setNormalCurrentPage(prevPage);
      fetchNormalAuditScores(selectedCampaignFilter, prevPage, normalItemsPerPage);
    }
  };
  const onPageChangeNormal = (page: number) => {
    if (normalCurrentPage !== page) {
      setNormalCurrentPage(page);
      fetchNormalAuditScores(selectedCampaignFilter, page, normalItemsPerPage);
    }
  };
  const handleItemsPerPageChangeNormal = (value: number) => {
    setNormalItemsPerPage(value);
    setNormalCurrentPage(1);
    fetchNormalAuditScores(selectedCampaignFilter, 1, value);
  };
  const handleFirstPageIntegrated = () => {
    if (integratedCurrentPage !== 1) {
      setIntegratedCurrentPage(1);
      fetchIntegratedAuditScores(selectedCampaignFilter, 1, integratedItemsPerPage);
    }
  };
  const handleLastPageIntegrated = () => {
    const lastPage = integratedTotalPages;
    if (integratedCurrentPage !== lastPage && lastPage > 0) {
      setIntegratedCurrentPage(lastPage);
      fetchIntegratedAuditScores(selectedCampaignFilter, lastPage, integratedItemsPerPage);
    }
  };
  const handleNextPageIntegrated = () => {
    if (integratedCurrentPage < integratedTotalPages) {
      const nextPage = integratedCurrentPage + 1;
      setIntegratedCurrentPage(nextPage);
      fetchIntegratedAuditScores(selectedCampaignFilter, nextPage, integratedItemsPerPage);
    }
  };
  const handlePreviousPageIntegrated = () => {
    if (integratedCurrentPage > 1) {
      const prevPage = integratedCurrentPage - 1;
      setIntegratedCurrentPage(prevPage);
      fetchIntegratedAuditScores(selectedCampaignFilter, prevPage, integratedItemsPerPage);
    }
  };
  const onPageChangeIntegrated = (page: number) => {
    if (integratedCurrentPage !== page) {
      setIntegratedCurrentPage(page);
      fetchIntegratedAuditScores(selectedCampaignFilter, page, integratedItemsPerPage);
    }
  };
  const handleItemsPerPageChangeIntegrated = (value: number) => {
    setIntegratedItemsPerPage(value);
    setIntegratedCurrentPage(1);
    fetchIntegratedAuditScores(selectedCampaignFilter, 1, value);
  };
  const renderPagination = (type: 'normal' | 'integrated', currentPage: number, itemsPerPage: number, totalElements: number, totalPages: number) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalElements);
    const pageNumbers = [];
    const maxPageNumbers = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
    if (endPage - startPage + 1 < maxPageNumbers) {
      startPage = Math.max(1, endPage - maxPageNumbers + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return (
      <div className="pagination">
        <button className="info">Showing {startItem} to {endItem} of {totalElements} items</button>
        <div className="pagination-controls">
          <button onClick={() => type === 'normal' ? handleFirstPageNormal() : handleFirstPageIntegrated()} className="pagination-btn" disabled={currentPage === 1}><span className="small-pipe">|</span>&#8249;</button>
          <button onClick={() => type === 'normal' ? handlePreviousPageNormal() : handlePreviousPageIntegrated()} className="pagination-btn" disabled={currentPage === 1}>&#8249;</button>
          {pageNumbers.map((page) => (
            <button key={`page-${type}-${page}`} onClick={() => type === 'normal' ? onPageChangeNormal(page) : onPageChangeIntegrated(page)} className={`pagination-btn ${currentPage === page ? "active" : ""}`}>{page}</button>
          ))}
          <button onClick={() => type === 'normal' ? handleNextPageNormal() : handleNextPageIntegrated()} className="pagination-btn" disabled={currentPage === totalPages}>&#8250;</button>
          <button onClick={() => type === 'normal' ? handleLastPageNormal() : handleLastPageIntegrated()} className="pagination-btn" disabled={currentPage === totalPages}>&#8250;<span className="small-pipe">|</span></button>
        </div>
        <div>
          <select value={itemsPerPage} onChange={(e) => type === 'normal' ? handleItemsPerPageChangeNormal(Number(e.target.value)) : handleItemsPerPageChangeIntegrated(Number(e.target.value))} className="items-perpage-select">
            <option value={5}>5</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    );
  };
  useEffect(() => {
    isMounted.current = true;
    const fetchData = async () => {
      const clientOnlyPayload: { field: string; operator: string; value: string }[] = [{ field: "clientId", operator: "=", value: clientId ?? "" }];
      const fullPayload = [...clientOnlyPayload];
      const userPayload = buildUserPayload();
      if (userPayload) fullPayload.push(userPayload);
      try {
        setLoading({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, additionalSummary: true, additionalFindings: true, auditScores: true, normalAuditScores: false, integratedAuditScores: false });
        setErrors({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, additionalSummary: false, additionalFindings: false, auditScores: false, normalAuditScores: false, integratedAuditScores: false });
        const [summaryResponse, additionalResponse] = await Promise.all([
          apiCall("post", `/api/dashboard/summary?clientId=${clientId}`, fullPayload).catch(err => {
            console.error("Summary fetch error:", err);
            setErrors(prev => ({ ...prev, auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true }));
            return null;
          }),
          apiCall("post", `/api/dashboard/summary?clientId=${clientId}`, clientOnlyPayload).catch(err => {
            console.error("Additional summary fetch error:", err);
            setErrors(prev => ({ ...prev, additionalSummary: true, additionalFindings: true }));
            return null;
          })
        ]);
        setData(summaryResponse);
        setAdditionalData(additionalResponse);
      } catch (err) {
        console.error("Fetch error:", err);
        setErrors({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, additionalSummary: true, additionalFindings: true, auditScores: true, normalAuditScores: true, integratedAuditScores: true });
      } finally {
        setLoading(prev => ({ ...prev, auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, additionalSummary: false, additionalFindings: false }));
      }
    };
    fetchData();
    return () => {
      isMounted.current = false;
      const currentTimeouts = timeoutRefs.current;
      currentTimeouts.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, [clientId, fetchNormalAuditScores, fetchIntegratedAuditScores]);
  const integratedControlTypes = getAllControlTypes(integratedAuditScores);
  const findingsData: Array<{ name: string; value: number; color: string }> = [
    { name: 'Not Implemented', value: data?.findings?.notImplemented || 0, color: '#9E9E9E' },
    { name: 'Initial / Ad-hoc', value: data?.findings?.initialAdhoc || 0, color: '#FFB74D' },
    { name: 'Partially Implemented', value: data?.findings?.partiallyImplemented || 0, color: '#FFD54F' },
    { name: 'Implemented but Needs Improvement', value: data?.findings?.improvements || 0, color: '#2196F3' },
    { name: 'Largely Effective', value: data?.findings?.largelyEffective || 0, color: '#4DB6AC' },
    { name: 'Best Practice / Optimized', value: data?.findings?.bestPractices || 0, color: '#4CAF50' },
  ];
  const hasFindingsData = findingsData.some((item: { name: string; value: number; color: string }) => item.value > 0);
  const findingsStatusData: Array<{ name: string; value: number; color: string }> = [
    { name: 'Closed', value: data?.findings?.ncCurrentStatus?.closed || 0, color: '#4CAF50' },
    { name: 'Open', value: data?.findings?.ncCurrentStatus?.open || 0, color: '#FFC107' },
    { name: 'Deferred', value: data?.findings?.ncCurrentStatus?.deferred || 0, color: '#9C27B0' },
    { name: 'Work in Progress', value: data?.findings?.ncCurrentStatus?.work_in_progress || 0, color: '#b13e08ff' },
  ].filter((item: { name: string; value: number; color: string }) => item.value > 0);
  const hasFindingsStatusData = findingsStatusData.some((item: { name: string; value: number; color: string }) => item.value > 0);
  const additionalFindingsData: Array<{ name: string; value: number; color: string }> = [
    { name: 'Not Implemented', value: additionalData?.findings?.notImplemented || 0, color: '#9E9E9E' },
    { name: 'Initial / Ad-hoc', value: additionalData?.findings?.initialAdhoc || 0, color: '#FFB74D' },
    { name: 'Partially Implemented', value: additionalData?.findings?.partiallyImplemented || 0, color: '#FFD54F' },
    { name: 'Implemented but Needs Improvement', value: additionalData?.findings?.improvements || 0, color: '#2196F3' },
    { name: 'Largely Effective', value: additionalData?.findings?.largelyEffective || 0, color: '#4DB6AC' },
    { name: 'Best Practice / Optimized', value: additionalData?.findings?.bestPractices || 0, color: '#4CAF50' },
  ];
  const hasAdditionalFindingsData = additionalFindingsData.some((item: { name: string; value: number; color: string }) => item.value > 0);
  const additionalFindingsStatusData: Array<{ name: string; value: number; color: string }> = [
    { name: 'Closed', value: additionalData?.findings?.ncCurrentStatus?.closed || 0, color: '#4CAF50' },
    { name: 'Open', value: additionalData?.findings?.ncCurrentStatus?.open || 0, color: '#FFC107' },
    { name: 'Deferred', value: additionalData?.findings?.ncCurrentStatus?.deferred || 0, color: '#9C27B0' },
    { name: 'Work in Progress', value: additionalData?.findings?.ncCurrentStatus?.work_in_progress || 0, color: '#b13e08ff' },
  ].filter((item: { name: string; value: number; color: string }) => item.value > 0);
  const hasAdditionalFindingsStatusData = additionalFindingsStatusData.some((item: { name: string; value: number; color: string }) => item.value > 0);
  const controlsData: Array<{ name: string; value: number; color: string }> = data?.controlCounts ? [
    { name: 'AIMS', value: data.controlCounts.aims || 0, color: '#8E24AA' },
    { name: 'QMS', value: data.controlCounts.qms || 0, color: '#D81B60' },
    { name: 'BCMS', value: data.controlCounts.bcms || 0, color: '#00897B' },
    { name: 'CMMI', value: data.controlCounts.cmmi || 0, color: '#5C6BC0' },
    { name: 'PIMS', value: data.controlCounts.pims || 0, color: '#F57C00' },
    { name: 'ISMS', value: data.controlCounts.isms || 0, color: '#43A047' },
    { name: 'SOC1', value: data.controlCounts.soc1 || 0, color: '#E53935' },
    { name: 'SOC2', value: data.controlCounts.soc2 || 0, color: '#039BE5' },
    { name: 'ERM', value: data.controlCounts.erm || 0, color: '#6D4C41' },
    { name: 'ITSM', value: data.controlCounts.itsm || 0, color: '#1dd5ee' },
    { name: 'ERS', value: data.controlCounts.ers || 0, color: '#9ef83f' },
    { name: 'OHS', value: data.controlCounts.ohs || 0, color: '#802c12' },
  ] : [];
  const hasControlsData = controlsData.some((item: { name: string; value: number; color: string }) => item.value > 0);
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };
  const renderNoData = (height = 200) => (
    <div className="no-data-available">No data available</div>
  );
  const getFindingClassName = (findingType: string, value: number) => {
    if (value === 0) return "rating-zero";
    switch (findingType) {
      case "BEST_PRACTICE":
        return "rating-optimized";
      case "LARGELY_EFFECTIVE":
        return "rating-effective";
      case "NEEDS_IMPROVMENT":
        return "rating-needs-improvement";
      case "PARTIALLY_IMPLEMENTED":
        return "rating-partially-implemented";
      case "INITIAL_ADHOC":
        return "rating-initial-adhoc";
      case "NOT_IMPLEMENTED":
        return "rating-high-risk";
      default:
        return "rating-default";
    }
  };
  const findingOrder = ["BEST_PRACTICE", "LARGELY_EFFECTIVE", "NEEDS_IMPROVMENT", "PARTIALLY_IMPLEMENTED", "INITIAL_ADHOC", "NOT_IMPLEMENTED"];
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Quality Consultant Dashboard</h1>
        <div className="dashboard-last-login">Last login: {new Date().toLocaleString()}</div>
      </div>
      <nav className="admin-dashboard-section-title">Over All View</nav>
      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Over All Audit Summary</h2>
          {loading.additionalSummary ? (
            <LoadingSpinner height={318} />
          ) : errors.additionalSummary || !additionalData ? (
            renderNoData(318)
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{additionalData?.completedAudits || 0}</span>
                  <span className="dashboard-metric-label">Completed Audits</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{additionalData?.assignedAudits || 0}</span>
                  <span className="dashboard-metric-label">Assigned Audits</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
                <div className="flex-1">
                  {hasAdditionalFindingsData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={additionalFindingsData} cx="50%" cy="50%" outerRadius={50} dataKey="value">
                          {additionalFindingsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                            <Cell key={`qc-overall-findings-cell-${index}-${entry.name}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : renderNoData(200)}
                </div>
                <div className="flex-1">
                  {additionalFindingsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                    <div key={`findings-legend-${index}-${entry.name}`} className="qc-overall-findings-legend-item">
                      <div className="findings-legend-color" style={{ backgroundColor: entry.color }} />
                      <span className="findings-legend-text"> {entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Over All Findings Status</h2>
          {loading.additionalFindings ? (
            <LoadingSpinner height={200} />
          ) : errors.additionalFindings || !hasAdditionalFindingsStatusData ? (
            renderNoData(200)
          ) : (
            <ResponsiveContainer width="95%" height={200} className="chart-mt-50">
              <BarChart data={additionalFindingsStatusData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value">
                  {additionalFindingsStatusData.map((entry: { name: string; value: number; color: string }, index: number) => (
                    <Cell key={`qc-overall-findings-status-cell-${index}-${entry.name}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Control Counts</h2>
          <div className="dashboard-metric-row">
            <div className="dashboard-metric-item">
              <span className="dashboard-metric-value">{additionalData?.completedAudits || 0}</span>
              <span className="dashboard-metric-label">Completed Audits</span>
            </div>
            <div className="dashboard-metric-item">
              <span className="dashboard-metric-value">{additionalData?.assignedAudits || 0}</span>
              <span className="dashboard-metric-label">Assigned Audits</span>
            </div>
          </div>
          {loading.auditSummary ? (
            <LoadingSpinner height={318} />
          ) : errors.auditSummary || !hasControlsData ? (
            renderNoData(318)
          ) : (
            <div className="dashboard-chart-container">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={controlsData} cx="50%" cy="50%" outerRadius={50} dataKey="value">
                      {controlsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                        <Cell key={`qc-controls-cell-${index}-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1">
                {controlsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                  <div key={`controls-legend-${index}-${entry.name}`} className="controls-legend-item">
                    <div className="controls-legend-color" style={{ backgroundColor: entry.color }} />
                    <span className="controls-legend-text"> {entry.name}: {entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      <nav className="admin-dashboard-section-title">Individual View</nav>
      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Audit Summary</h2>
          {loading.auditSummary ? (
            <LoadingSpinner height={318} />
          ) : errors.auditSummary || !data ? (
            renderNoData(318)
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.completedAudits || 0}</span>
                  <span className="dashboard-metric-label">Completed Audits</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.assignedAudits || 0}</span>
                  <span className="dashboard-metric-label">Assigned Audits</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
                <div className="flex-1">
                  {hasFindingsData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={findingsData} cx="50%" cy="50%" outerRadius={50} dataKey="value">
                          {findingsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                            <Cell key={`qc-individual-findings-cell-${index}-${entry.name}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : renderNoData(200)}
                </div>
                <div className="flex-1">
                  {findingsData.map((entry: { name: string; value: number; color: string }, index: number) => (
                    <div key={`findings-legend-${index}-${entry.name}`} className="qc-individual-findings-legend-item">
                      <div className="findings-legend-color" style={{ backgroundColor: entry.color }} />
                      <span className="findings-legend-text"> {entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Findings Status</h2>
          {loading.findingsStatus ? (
            <LoadingSpinner height={200} />
          ) : errors.findingsStatus || !hasFindingsStatusData ? (
            renderNoData(200)
          ) : (
            <ResponsiveContainer width="95%" height={200} className="chart-mt-50">
              <BarChart data={findingsStatusData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value">
                  {findingsStatusData.map((entry: { name: string; value: number; color: string }, index: number) => (
                    <Cell key={`qc-individual-findings-status-cell-${index}-${entry.name}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Current Projects</h2>
          {loading.projectChanges ? (
            <LoadingSpinner height={200} />
          ) : errors.projectChanges || !data?.currentProjectChanges ? (
            renderNoData(200)
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.overall?.excluded ?? '0'}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Overall)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.overall?.additional ?? '0'}</span>
                  <span className="dashboard-metric-label">Additional Projects (Overall)</span>
                </div>
              </div>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.currentCampaign?.excluded ?? '0'}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Current Campaign)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.currentCampaign?.additional ?? '0'}</span>
                  <span className="dashboard-metric-label">Additional Projects (Current Campaign)</span>
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card dashboard-full-width-card">
          <h2 className="dashboard-card-title">Projects Assigned</h2>
          {loading.projectsAssigned ? (
            <LoadingSpinner height={400} />
          ) : errors.projectsAssigned || !data?.currentProjects ? (
            renderNoData(400)
          ) : (
            <div className="dashboard-projects-container">
              {data.currentProjects.length > 0 ? (
                data.currentProjects.map((project: any) => (
                  <div key={`qc-project-${project.projectId}`} className="dashboard-project-item">
                    <div className="dashboard-project-name">Project ID: {project.projectId}</div>
                    <div className={`dashboard-project-status dashboard-project-status-${(project.status || '').toLowerCase().replace('_', '-')}`}>Project Status : {project.status.replace('_', ' ')}</div>
                    <div className="dashboard-project">Start Date: {project.startDate ? formatDate(project.startDate) : 'Not Applicable'}</div>
                    <div className="dashboard-project">End Date: {project.endDate ? formatDate(project.endDate) : 'Not Applicable'}</div>
                    <div className="dashboard-project"> Report Submitted: {project.reportSubmitted === true ? "Yes" : project.reportSubmitted === false ? "No" : project.reportSubmitted === null ? "Not Applicable" : formatDate(project.reportSubmitted)}</div>
                  </div>
                ))
              ) : (
                renderNoData(400)
              )}
            </div>
          )}
        </Card>
      </div>
      <nav className="admin-dashboard-section-title">Integrated Audit Performance Scores</nav>
      <Card className="dashboard-card dashboard-full-width-card">
        <div className="audit-scores-header">
          <h2 className="dashboard-card-title">Integrated Audit Scores</h2>
          <FormControl className="campaign-filter-control" size="small">
            <InputLabel id="campaign-filter-label">Select Campaign</InputLabel>
            <Select labelId="campaign-filter-label" id="campaign-filter" value={selectedCampaignFilter} label="Select Campaign" onChange={handleCampaignFilterChange}>
              <MenuItem value="all">All Campaigns</MenuItem>
              {getUniqueCampaignNames().map((campaignName) => (
                <MenuItem key={campaignName} value={campaignName}>{campaignName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {integratedLoading ? (
          <LoadingSpinner height={400} />
        ) : integratedAuditScores.length === 0 ? (
          <div className="no-data-available">No integrated audit scores data available
            {selectedCampaignFilter !== "all" && ` for campaign: ${selectedCampaignFilter}`}
          </div>
        ) : (
          <>
            <TableContainer component={Paper} className="campaigns-table-container">
              <Table>
                <TableHead className="campaigns-table-header">
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell align="right">Overall Score</TableCell>
                    <TableCell align="center">Overall Rating</TableCell>
                    {integratedControlTypes.map((controlType) => (
                      <React.Fragment key={controlType}>
                        <TableCell align="right">{controlType} Weighted</TableCell>
                        <TableCell align="center">{controlType} Rating</TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell align="center">View Radar & Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="campaigns-table-body">
                  {integratedAuditScores.map((auditScore, index) => {
                    const auditScoreData = auditScore.auditScoreData as any;
                    const overallScore = auditScoreData?.overallScore || 0;
                    const overallRating = auditScoreData?.overallRating || 'N/A';
                    const overallRatingClassName = getRatingClassName(overallRating);
                    return (
                      <TableRow key={`integrated-${auditScore.auditId || index}`} hover className="campaign-body-row">
                        <TableCell>{auditScore.campaignName}</TableCell>
                        <TableCell>{auditScore.projectName}</TableCell>
                        <TableCell align="right">
                          <span className="overall-score">{overallScore.toFixed(2)}%</span>
                        </TableCell>
                        <TableCell align="center">
                          <span className={`rating-badge ${overallRatingClassName}`}>{overallRating}</span>
                        </TableCell>
                        {integratedControlTypes.map((controlType) => {
                          const control = getControlScoreByType(auditScore, controlType);
                          const ratingClassName = getRatingClassName(control?.rating || '');
                          return (
                            <React.Fragment key={`${controlType}-${auditScore.auditId || index}`}>
                              <TableCell align="right">
                                <span className="weighted-score">{control ? formatWeightedScore(control.weightedScore) : 'N/A'}</span>
                              </TableCell>
                              <TableCell align="center">
                                <span className={`rating-badge ${ratingClassName}`}>{control?.rating || 'N/A'}</span>
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell align="center">
                          <button className="radar-view-button" onClick={() => handleProjectClick(auditScore.projectId, auditScore.auditId, auditScore.projectName, auditScore.checklistType, auditScore.campaignId, auditScore.campaignName)}>View Radar & Findings</button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderPagination('integrated', integratedCurrentPage, integratedItemsPerPage, integratedTotalElements, integratedTotalPages)}
          </>
        )}
      </Card>
      <nav className="admin-dashboard-section-title">Normal Audit Performance Scores</nav>
      <Card className="dashboard-card dashboard-full-width-card">
        <div className="audit-scores-header">
          <h2 className="dashboard-card-title">Normal Audit Scores</h2>
          <FormControl className="campaign-filter-control" size="small">
            <InputLabel id="campaign-filter-label-normal">Select Campaign</InputLabel>
            <Select labelId="campaign-filter-label-normal" id="campaign-filter-normal" value={selectedCampaignFilter} label="Select Campaign" onChange={handleCampaignFilterChange}>
              <MenuItem value="all">All Campaigns</MenuItem>
              {getUniqueCampaignNames().map((campaignName) => (
                <MenuItem key={campaignName} value={campaignName}>{campaignName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {normalLoading ? (
          <LoadingSpinner height={400} />
        ) : normalAuditScores.length === 0 ? (
          <div className="no-data-available">No normal audit scores data available{selectedCampaignFilter !== "all" ? ` for campaign: ${selectedCampaignFilter}` : ''}</div>
        ) : (
          <>
            <TableContainer component={Paper} className="campaigns-table-container">
              <Table>
                <TableHead className="campaigns-table-header">
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell align="right">Overall Score</TableCell>
                    <TableCell align="center">Overall Rating</TableCell>
                    <TableCell align="center">View Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="campaigns-table-body">
                  {normalAuditScores.map((auditScore: AuditScore, index: number) => {
                    const auditScoreData = auditScore.auditScoreData as any;
                    const overallScore = auditScoreData?.overallScore || 0;
                    const overallRating = auditScoreData?.overallRating || 'N/A';
                    const overallRatingClassName = getRatingClassName(overallRating);
                    return (
                      <TableRow key={`normal-${auditScore.auditId || index}`} hover className="campaign-body-row">
                        <TableCell>{auditScore.campaignName}</TableCell>
                        <TableCell>{auditScore.projectName}</TableCell>
                        <TableCell align="right"><span className="overall-score"> {overallScore.toFixed(2)}%</span></TableCell>
                        <TableCell align="center"><span className={`rating-badge ${overallRatingClassName}`}> {overallRating} </span></TableCell>
                        <TableCell align="center">
                          <button className="radar-view-button" onClick={() => handleProjectClick(auditScore.projectId, auditScore.auditId, auditScore.projectName, auditScore.checklistType, auditScore.campaignId, auditScore.campaignName)}>View Findings</button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderPagination('normal', normalCurrentPage, normalItemsPerPage, normalTotalElements, normalTotalPages)}
          </>
        )}
      </Card>
      <Dialog open={radarDialogOpen} onClose={handleCloseRadarDialog} maxWidth="xl" fullWidth>
        <DialogTitle className="radar-dialog-title">
          <div className="radar-dialog-header">
            <span>Control Performance - Project: {selectedProject?.projectName}</span>
            <IconButton onClick={handleCloseRadarDialog} className="close-button">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="radar-dialog-content">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Control Performance Radar</h3>
              {selectedProjectRadarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={selectedProjectRadarData}>
                    <PolarGrid gridType="polygon" />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={7} />
                    <Radar name="Score" dataKey="score" stroke="#8884d8" strokeWidth={2} fill="#8884d8" fillOpacity={0.3} dot={(props: any) => {
                      const { cx, cy, index } = props;
                      return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                    }} />
                    <Radar name="Weighted Score" dataKey="weightedScore" stroke="#82ca9d" strokeWidth={2} fill="#82ca9d" fillOpacity={0.3} dot={(props: any) => {
                      const { cx, cy, index } = props;
                      return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                    }} />
                    <Legend />
                    <Tooltip formatter={(value: number, name: string) => [value.toFixed(2), name]} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No radar data available for this project</div>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Findings by Control Type</h3>
              {integratedFindingsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              ) : integratedFindingsData?.findingsByControls && Object.keys(integratedFindingsData.findingsByControls).length > 0 ? (
                <TableContainer component={Paper} style={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Control Type</TableCell>
                        <TableCell align="center">Best Practice</TableCell>
                        <TableCell align="center">Largely Effective</TableCell>
                        <TableCell align="center">Needs Improvement</TableCell>
                        <TableCell align="center">Partially Implemented</TableCell>
                        <TableCell align="center">Initial / Adhoc</TableCell>
                        <TableCell align="center">Not Implemented</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(integratedFindingsData.findingsByControls).map(([controlType, findings]: [string, any]) => (
                        <TableRow key={controlType}>
                          <TableCell component="th" scope="row">
                            <strong>{controlType}</strong>
                          </TableCell>
                          {findingOrder.map((findingKey) => (
                            <TableCell key={`${controlType}-${findingKey}`} align="center" className={getFindingClassName(findingKey, findings[findingKey] || 0)}>
                              {findings[findingKey] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <div className="no-data">No findings data available for this project</div>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={findingsDialogOpen} onClose={handleCloseFindingsDialog} maxWidth="lg" fullWidth>
        <DialogTitle className="radar-dialog-title">
          <div className="radar-dialog-header">
            <span>Findings - Project: {selectedProject?.projectName} (Campaign: {selectedProject?.campaignName})</span>
            <IconButton onClick={handleCloseFindingsDialog} className="close-button">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="radar-dialog-content">
          {findingsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : projectFindingsData?.findingsByPracticeArea && Object.keys(projectFindingsData.findingsByPracticeArea).length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Practice Area</TableCell>
                    <TableCell align="center">Best Practice</TableCell>
                    <TableCell align="center">Largely Effective</TableCell>
                    <TableCell align="center">Needs Improvement</TableCell>
                    <TableCell align="center">Partially Implemented</TableCell>
                    <TableCell align="center">Initial / Adhoc</TableCell>
                    <TableCell align="center">Not Implemented</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(projectFindingsData.findingsByPracticeArea).map(([practiceArea, findings]: [string, any]) => (
                    <TableRow key={practiceArea}>
                      <TableCell component="th" scope="row">
                        <strong>{practiceArea}</strong>
                      </TableCell>
                      {findingOrder.map((findingKey) => (
                        <TableCell key={`${practiceArea}-${findingKey}`} align="center" className={getFindingClassName(findingKey, findings[findingKey] || 0)}>
                          {findings[findingKey] || 0}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="no-data">No findings data available for this project</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default QCDashboard;